import React, { useEffect, useState } from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import { Navigation } from './Navigation';
import { MobileBottomNav } from './MobileBottomNav';
import CookieConsent from '../CookieConsent';
import { useAuth } from '../../context/AuthContext';
import { useHideFooterContext } from '../../context/HideFooterContext';
import { supabase } from '../../lib/supabase';
import { X, Mail, Loader2 } from 'lucide-react';
import { SiteStaffBanner } from '../site/SiteStaffBanner';
import { SiteStaffPageModule } from '../site/SiteStaffPageModule';
import { applyDashboardPrefsToDocument } from '../../lib/dashboardPrefsApply';
import { readDashboardPrefsFromLocalStorage } from '../../lib/dashboardPrefsStorage';
import { isMainSiteUnlocked, isPublicMainSitePath } from '../../lib/mainSiteAccess';
import { useMainSiteAccessGate } from '../../hooks/useMainSiteAccessGate';
import { usePresenceHeartbeat } from '../../hooks/usePresenceHeartbeat';
import { useMobileAppChrome } from '../../hooks/useMobileAppChrome';
import { MobilePageTransition } from './MobilePageTransition';

const FOOTERLESS_PATHS = ['/messages'];

/** No extra main bottom padding — child uses full viewport between header and tab bar (or full screen). */
const COMPACT_MAIN_PREFIXES = ['/messages', '/post/new', '/operations', '/warehouse', '/admin', '/sell'];

interface PlatformLayoutProps {
  children: React.ReactNode;
}

const EmailVerificationBanner: React.FC = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user.emailVerified || dismissed) return null;

  const handleResend = async () => {
    setResending(true);
    await supabase.auth.resend({ type: 'signup', email: user.email });
    setResending(false);
    setSent(true);
  };

  return (
    <div className="bg-yellow-900/40 border-b border-yellow-800/50 px-4 py-2.5">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Mail size={14} className="text-yellow-400 flex-shrink-0" />
          <p className="text-xs text-yellow-200 truncate">
            {sent ? 'Verification email sent! Check your inbox.' : 'Please verify your email to unlock all features.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!sent && (
            <button onClick={handleResend} disabled={resending} className="text-xs font-medium text-yellow-300 hover:text-yellow-100 transition-colors whitespace-nowrap">
              {resending ? <Loader2 size={12} className="animate-spin" /> : 'Resend'}
            </button>
          )}
          <button onClick={() => setDismissed(true)} className="p-0.5 text-yellow-500 hover:text-yellow-300">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const PlatformLayout: React.FC<PlatformLayoutProps> = ({ children }) => {
  const { pathname, search } = useLocation();
  const { user, session } = useAuth();
  const { lockEnabled } = useMainSiteAccessGate();
  const prefsUserId = user?.id ?? session?.user?.id;
  usePresenceHeartbeat(prefsUserId);
  useMobileAppChrome();
  const [userHideFooter] = useHideFooterContext();
  const hideFooter = userHideFooter || FOOTERLESS_PATHS.some(p => pathname.startsWith(p));
  const compactMain = COMPACT_MAIN_PREFIXES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    const apply = () => {
      const p = readDashboardPrefsFromLocalStorage(prefsUserId);
      applyDashboardPrefsToDocument(p);
    };
    apply();
    const onEvt = () => apply();
    window.addEventListener('d88-control-prefs-changed', onEvt as EventListener);
    window.addEventListener('storage', onEvt);
    return () => {
      window.removeEventListener('d88-control-prefs-changed', onEvt as EventListener);
      window.removeEventListener('storage', onEvt);
    };
  }, [prefsUserId]);

  if (lockEnabled && !isPublicMainSitePath(pathname) && !isMainSiteUnlocked()) {
    const returnTo = encodeURIComponent(`${pathname}${search || ''}`);
    return <Navigate to={`/?returnTo=${returnTo}`} replace />;
  }

  return (
    <div className="min-h-[100dvh] bg-district-black overflow-x-hidden w-full max-w-[100vw] max-lg:overscroll-y-none">
      {/* Grain Overlay */}
      <div className="grain-overlay" />

      <Navigation />

      {/* Email Verification Banner */}
      <div className="pt-[calc(3.5rem+env(safe-area-inset-top,0px))] lg:pt-20">
        <EmailVerificationBanner />
        <SiteStaffBanner />
        <SiteStaffPageModule />
      </div>

      {/* Main Content — bottom padding on small screens for tab bar + home indicator */}
      <main
        className={`d88-main overflow-x-hidden max-lg:min-h-0 lg:pb-0 ${
          compactMain
            ? 'pb-0'
            : 'pb-[calc(4.25rem+env(safe-area-inset-bottom,0px))]'
        }`}
      >
        <MobilePageTransition>{children}</MobilePageTransition>
      </main>

      <MobileBottomNav />

      {/* Footer */}
      {!hideFooter && (
      <footer className="hidden lg:block border-t border-white/[0.06] mt-12 sm:mt-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto min-w-0 px-3 sm:px-6 lg:px-12 py-8 sm:py-12 lg:py-14">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-10 lg:gap-12">
            <div className="col-span-2 md:col-span-1 lg:col-span-2">
              <h3 className="text-lg sm:text-xl font-semibold tracking-tight text-district-white mb-3">
                DISTRICT<span className="text-district-red">88</span>
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-sm">
                Street-rooted commerce and culture—structured for clarity.
              </p>
              <a href="/explore" className="inline-block mt-4 text-sm font-medium text-district-red hover:text-red-400 transition-colors">
                Full site map →
              </a>
            </div>

            {/* Browse */}
            <div>
              <h4 className="text-label text-district-street mb-3">Browse</h4>
              <ul className="space-y-2">
                <li><a href="/explore" className="text-sm text-gray-400 hover:text-district-white transition-colors">Explore all pages</a></li>
                <li><a href="/district" className="text-sm text-gray-400 hover:text-district-white transition-colors">Home</a></li>
                <li><a href="/drops" className="text-sm text-gray-400 hover:text-district-white transition-colors">Drops</a></li>
                <li><a href="/market" className="text-sm text-gray-400 hover:text-district-white transition-colors">Market</a></li>
              </ul>
            </div>

            {/* Culture */}
            <div>
              <h4 className="text-label text-district-street mb-3">Culture</h4>
              <ul className="space-y-2">
                <li><a href="/connect" className="text-sm text-gray-400 hover:text-district-white transition-colors">Connect</a></li>
                <li><a href="/culture" className="text-sm text-gray-400 hover:text-district-white transition-colors">Culture</a></li>
                <li><a href="/learn" className="text-sm text-gray-400 hover:text-district-white transition-colors">Learn</a></li>
                <li><a href="/connect?section=clips" className="text-sm text-gray-400 hover:text-district-white transition-colors">Clips</a></li>
              </ul>
            </div>

            {/* Commerce & connect */}
            <div>
              <h4 className="text-label text-district-street mb-3">Commerce</h4>
              <ul className="space-y-2">
                <li><a href="/sell" className="text-sm text-gray-400 hover:text-district-white transition-colors">Sell</a></li>
                <li><a href="/cart" className="text-sm text-gray-400 hover:text-district-white transition-colors">Cart</a></li>
                <li><a href="/support" className="text-sm text-gray-400 hover:text-district-white transition-colors">Support</a></li>
                <li><a href="/trust" className="text-sm text-gray-400 hover:text-district-white transition-colors">Trust</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-white/[0.06] flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <p className="text-xs text-gray-500 tracking-tight">
              © 2026 District88. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <a href="/privacy" className="text-xs text-gray-500 hover:text-district-white transition-colors">Privacy</a>
              <a href="/terms" className="text-xs text-gray-500 hover:text-district-white transition-colors">Terms</a>
              <a href="/refunds" className="text-xs text-gray-500 hover:text-district-white transition-colors">Refunds</a>
              <a href="/careers" className="text-xs text-gray-500 hover:text-district-white transition-colors">Careers</a>
              <a href="mailto:info@joindistrict88.com" className="text-xs text-gray-500 hover:text-district-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      )}

      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
};

export default PlatformLayout;
